import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Gallery, GalleryRequest, PaginatedPhotos } from '../../../model/Carrusel';
import { Pageable, PaginatedCollection } from '../../../model/Pagination';
import HttpClient, { Callback, ExtraAction } from '../api';

const pageable: Pageable = {
  sort: {
    unsorted: true,
    sorted: false,
    empty: true,
  },
  offset: 0,
  pageNumber: 0,
  pageSize: 10,
  paged: true,
  unpaged: false,
};

const INITIAL_STATE: PaginatedCollection<Gallery> = {
  content: [],
  size: 0,
  totalElements: 0,
  totalPages: 0,
  number: 0,
  first: false,
  numberOfElements: 0,
  empty: false,
  last: false,
  pageable,
  category: '',
  sort: {
    unsorted: false,
    sorted: false,
    empty: false,
  },
};

const galleriesSlice = createSlice({
  name: 'galleries',
  initialState: INITIAL_STATE,
  reducers: {
    save: (state, action: PayloadAction<PaginatedCollection<Gallery>>) => ({
      ...state,
      ...action.payload,
    }),
    savePhotos: (state, action: PayloadAction<PaginatedPhotos>) => {
      const content = state.content.map((it) => {
        if (it.id === action.payload.galleryId) {
          const newState: Gallery = { ...it, photos: { ...action.payload } };
          return newState;
        }
        return it;
      });
      return { ...state, content: content };
    },
    removePhoto: (state, action: PayloadAction<{ galleryId: number; photoId: number }>) => {
      const content = state.content.map((gallery) => {
        if (gallery.id === action.payload.galleryId) {
          const updatedPhotos = gallery.photos.content.filter(
            (photo) => photo.id !== action.payload.photoId,
          );
          return {
            ...gallery,
            photos: {
              ...gallery.photos,
              content: updatedPhotos,
            },
          };
        }
        return gallery;
      });
      return { ...state, content };
    },
  },
});

export const { save, savePhotos, removePhoto } = galleriesSlice.actions;

export default galleriesSlice.reducer;

export const findByQuery = (
  extraActions?: Array<ExtraAction>,
  customErrorsMap = new Map<number, string>(),
  apiQuery = new Map<string, string>(),
) => {
  return HttpClient.findGalleriesByQuery(apiQuery, save.type, customErrorsMap, extraActions);
};

export const update = (gallery: Gallery, callbacks?: Array<Callback>) => {
  const galleryRequest: GalleryRequest = { ...gallery, photos: gallery.photos.content };
  return HttpClient.galleryUpdate(galleryRequest, save.type, callbacks);
};
export const create = (gallery: Gallery, callbacks?: Array<Callback>) => {
  const galleryRequest: GalleryRequest = { ...gallery, photos: gallery.photos.content };
  return HttpClient.galleryCreate(galleryRequest, save.type, callbacks);
};
export const remove = (gallery: Gallery, callbacks?: Array<Callback>) => {
  return HttpClient.galleryDelete(gallery, save.type, callbacks);
};

export const removePhotoById = (
  galleryId: number,
  photoId: number,
  callbacks?: Array<Callback>,
) => {
  const responseIsSuccess = (onSuccess: string): boolean => onSuccess === removePhoto.type;
  const extraAction: ExtraAction = {
    condition: responseIsSuccess,
    payload: { photoId, galleryId },
    actionName: removePhoto.type,
    preProccess: null,
  };
  return HttpClient.photoDelete(galleryId, photoId, removePhoto.type, callbacks, undefined, [
    extraAction,
  ]);
};

export const findPaginated = (name: string | null = '', page: number = 0) => {
  const customErrorsMap = new Map<number, string>();
  customErrorsMap.set(
    404,
    'A configuração do site precisa ser executada antes de abrir essa página',
  );
  const apiQuery = new Map<string, any>();
  apiQuery.set('page', page);

  if (name) {
    apiQuery.set('name', name);
  }

  return findByQuery([], customErrorsMap, apiQuery);
};

export const findPhotosPaginated = (
  galleryId: number,
  name: string | null = '',
  page: number = 0,
) => {
  const customErrorsMap = new Map<number, string>();
  customErrorsMap.set(
    404,
    'A configuração do site precisa ser executada antes de abrir essa página',
  );
  const apiQuery = new Map<string, any>();
  apiQuery.set('page', page);

  if (name) {
    apiQuery.set('name', name);
  }
  const extraActions: Array<ExtraAction> = [];
  return HttpClient.findPhotosByQuery(
    galleryId,
    apiQuery,
    savePhotos.type,
    customErrorsMap,
    extraActions,
  );
};
